import { errorHelpers } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { backend, domain } from 'modules/data';

import { messages } from './OffboardUserPopupContent.messages';
import { GLOBAL_USER_REPLACEMENT, OffboardUserInfoAnswer } from './OffboardUserPopupContent.types';

export const getRoleDisplayName = (role: backend.ParticipantRole) => {
    switch (role) {
        case backend.ParticipantRole.Approver:
            return messages.approver;

        case backend.ParticipantRole.DefaultApprover:
            return messages.defaultApprover;

        case backend.ParticipantRole.ExternalRequester:
            return messages.externalRequester;

        case backend.ParticipantRole.Requester:
            return messages.requester;

        case backend.ParticipantRole.Reviewer:
            return messages.reviewer;

        case backend.ParticipantRole.Editor:
            return messages.editor;

        case backend.ParticipantRole.DextExternalRequester:
            return messages.dextExternalRequester;

        case backend.ParticipantRole.EmailExternalRequester:
            return messages.emailExternalRequester;

        case backend.ParticipantRole.Payer:
            return messages.payer;

        case backend.ParticipantRole.DefaultReviewer:
            return messages.defaultReviewer;

        default:
            throw errorHelpers.assertNever(role);
    }
};

export const isRequiredRoleToOffboard = (role: backend.ParticipantRole) => {
    return [
        backend.ParticipantRole.ExternalRequester,
        backend.ParticipantRole.DextExternalRequester,
        backend.ParticipantRole.EmailExternalRequester,
    ].includes(role);
};

export const isApproverRoleRequired = (
    workflow: domain.WorkflowsParticipant,
    selectedReplacements: {
        [key: string]: selectors.types.ExpandedUser;
    }
) =>
    workflow.hasOnlyApprover &&
    workflow.replacements.every(
        (replacement) =>
            replacement.role !== backend.ParticipantRole.Approver ||
            !selectedReplacements[getWorkflowReplacementId(replacement, workflow)]
    );

export const getWorkflowReplacementId = (
    replacement: domain.ParticipantStep,
    workflow: domain.WorkflowsParticipant
) => {
    return `${replacement.stepNumber}|${workflow.workflowId}|${replacement.role}`;
};

export const getRequestReplacementId = (replacement: domain.ParticipantStep, request: domain.RequestsParticipant) => {
    return `${replacement.stepNumber}|${request.requestId}`;
};

export const getWorkflowAutoApproveStepId = (workflow: domain.WorkflowsParticipant) => {
    return `auto-approve|${workflow.workflowId}`;
};

export const validateExternalRequesterInWorkflow = (
    offboardUserInfo: OffboardUserInfoAnswer,
    selectedDelegates: { [key: string]: selectors.types.ExpandedUser },
    isPayerOffboardingBetaFeature: boolean
) => {
    for (const workflow of offboardUserInfo.workflows) {
        const autoApprovalId = getWorkflowAutoApproveStepId(workflow);

        if (workflow.hasRequesterInAutoApprovalRules && !selectedDelegates[autoApprovalId]) {
            return false;
        }

        const requiredApproverRole = isApproverRoleRequired(workflow, selectedDelegates);

        if (requiredApproverRole) {
            return false;
        }

        for (const replacement of workflow.replacements) {
            const isReplacementRequired = isPayerOffboardingBetaFeature
                ? replacement.isReplacementRequired
                : isRequiredRoleToOffboard(replacement.role);

            if (isReplacementRequired) {
                /// replacement id is just an index. To make id uniq we add to index a id of parent entity
                const replacementId = getWorkflowReplacementId(replacement, workflow);
                const requiredReplacementFilled = selectedDelegates[replacementId];

                if (!requiredReplacementFilled) {
                    return false;
                }
            }
        }
    }

    return true;
};

export const validateExternalRequester = (
    offboardUserInfo: OffboardUserInfoAnswer,
    selectedDelegates: { [key: string]: selectors.types.ExpandedUser },
    isPayerOffboardingBetaFeature: boolean
) => {
    for (const workflow of offboardUserInfo.workflows) {
        for (const replacement of workflow.replacements) {
            const isReplacementRequired = isPayerOffboardingBetaFeature
                ? replacement.isReplacementRequired
                : isRequiredRoleToOffboard(replacement.role);

            if (isReplacementRequired) {
                const replacementId = getWorkflowReplacementId(replacement, workflow);
                const requiredReplacementFilled = selectedDelegates[replacementId];

                if (!requiredReplacementFilled) {
                    return false;
                }
            }
        }
    }

    for (const request of offboardUserInfo.requests) {
        for (const replacement of request.replacements) {
            const isReplacementRequired = isPayerOffboardingBetaFeature
                ? replacement.isReplacementRequired
                : request.hasOnlyApprover;

            if (isReplacementRequired) {
                const replacementId = getRequestReplacementId(replacement, request);
                const requiredReplacementFilled =
                    selectedDelegates[replacementId] || GLOBAL_USER_REPLACEMENT in selectedDelegates;

                if (!requiredReplacementFilled) {
                    return false;
                }
            }
        }
    }

    return true;
};
